import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import classNames from 'classnames';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { formatDateToText } from '../../util/dates';
import {
  Modal,
  NamedLink,
  ExternalLink,
  Avatar,
  RentedBadge,
  Pill,
  RsvpAvatarStack,
  Icon,
} from '../../components';
import { encodeChannelId } from '../../util/channelUrl';
import { CHANNEL_TYPES } from '../../util/sendbird';
import { daysBetween } from '../../util/dates';
import { manageDisableScrolling } from '../../ducks/UI.duck';
import { attendeeKey } from '../../ducks/episodeAttendees.duck';
import { createSlug, LISTING_PAGE_PARAM_TYPE_EDIT } from '../../util/urlHelpers';

import { INVITE_STATUS, inviteStatusOf } from '../../util/calendarInvites';
import css from './CalendarEventModal.module.css';
import { PAGE_TYPE } from '../../util/listingType';

// Attendee Item Component
const STATUS_CLASS = {
  [INVITE_STATUS.YES]: 'inviteStatusYes',
  [INVITE_STATUS.NO]: 'inviteStatusNo',
  [INVITE_STATUS.INVITED]: 'inviteStatusInvited',
  [INVITE_STATUS.NOT_SENT]: 'inviteStatusMuted',
  [INVITE_STATUS.NOT_INVITED]: 'inviteStatusMuted',
};

// Participants picked in the event form carry a `label`; the owner entry the
// calendar builds carries a `name` and a full user for the avatar.
const AttendeeItem = ({ attendee, isOwner, inviteEntry }) => {
  const { name, label, user } = attendee;
  const status = isOwner ? null : inviteStatusOf(inviteEntry);

  return (
    <div className={css.attendeeItem}>
      <Avatar
        user={user}
        className={css.attendeeAvatar}
        initialsClassName={css.attendeeAvatarInitials}
        disableProfileLink
      />
      <span className={css.attendeeName}>{name || label}</span>
      {isOwner && (
        <span className={css.ownerBadge}>
          <FormattedMessage id="CalendarEventModal.ownerBadge" />
        </span>
      )}
      {status && (
        <span className={classNames(css.inviteStatus, css[STATUS_CLASS[status]])}>
          <FormattedMessage id={`CalendarEventModal.inviteStatus.${status}`} />
        </span>
      )}
    </div>
  );
};

// Copy Link Button with feedback
const CopyLinkButton = ({ meetingLink, intl }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const fullLink = meetingLink.startsWith('/')
      ? `${window.location.origin}${meetingLink}`
      : meetingLink;
    navigator.clipboard.writeText(fullLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button type="button" className={css.copyLinkButton} onClick={handleCopy}>
      {copied
        ? intl.formatMessage({ id: 'CalendarEventModal.linkCopied' })
        : intl.formatMessage({ id: 'CalendarEventModal.copyLink' })}
    </button>
  );
};

// Generic Event Details Component
const EventDetails = props => {
  const { event, intl, timeZone, currentUserId, orgId, rsvpStack, invites } = props;

  const {
    type,
    title,
    listingTitle,
    start,
    end,
    location,
    meeting,
    description,
    attendees = [],
    ownerId,
  } = event;

  // Format date/time
  const startDate = start ? new Date(start) : null;
  const endDate = end ? new Date(end) : null;
  const formattedStart = startDate ? formatDateToText(intl, startDate, timeZone) : null;
  const formattedEnd = endDate ? formatDateToText(intl, endDate, timeZone) : null;

  // Format date range for shows
  const formatDateRange = () => {
    if (!startDate) return null;

    const currentYear = new Date().getFullYear();
    const startYear = startDate.getFullYear();
    const options = { weekday: 'long', month: 'long', day: 'numeric' };

    if (formattedEnd && startDate?.toDateString() === endDate?.toDateString()) {
      // Same day - show date once with time range
      return `${formattedStart.date}, ${formattedStart.time} - ${formattedEnd.time}`;
    } else if (formattedEnd) {
      // Different days - show date range
      const startFormatted = startDate.toLocaleDateString(intl.locale, options);
      const endFormatted = endDate.toLocaleDateString(intl.locale, options);
      const yearSuffix = startYear !== currentYear ? `, ${startYear}` : '';
      return `${startFormatted} - ${endFormatted}${yearSuffix}`;
    } else {
      return formattedStart?.dateAndTime;
    }
  };

  // Get meeting link. The event creator (host) is handed the Whereby host link
  // (host controls: lock room, admit/remove, recording); everyone else gets the
  // participant link.
  const getMeetingLink = () => {
    if (meeting?.roomName) {
      return `/meet/${meeting.roomName}`;
    }
    const isHost = !!currentUserId && meeting?.hostUserId === currentUserId;
    if (isHost && meeting?.hostUrl) return meeting.hostUrl;
    if (meeting?.hostRoomUrl) return meeting.hostRoomUrl;
    if (meeting?.roomUrl) return meeting.roomUrl;
    // Generated (or manually saved) participant link from the Create/Edit Event
    // modal, stored as meeting.url.
    if (meeting?.url) return meeting.url;
    return null;
  };

  const isMeetingHost = !!currentUserId && meeting?.hostUserId === currentUserId;
  const meetingLink = getMeetingLink();

  // The link shared via Copy is always the participant link — never the host
  // link (which grants host controls to whoever opens it).
  const getShareLink = () => {
    if (meeting?.roomName) return `/meet/${meeting.roomName}`;
    return meeting?.url || meeting?.roomUrl || meeting?.viewerRoomUrl || meetingLink;
  };
  const shareLink = getShareLink();
  const displayTitle =
    title || listingTitle || intl.formatMessage({ id: 'CalendarEventModal.untitledEvent' });
  const dateDisplay = formatDateRange();
  const hasAttendees = attendees && attendees.length > 0;

  // Answers at a glance for a manual event, the way a calendar shows them:
  // how many are going, declined, and still to answer.
  const inviteTotals = (() => {
    const out = { yes: 0, no: 0, pending: 0 };
    attendees.forEach(a => {
      if (a.id === ownerId || a.userId === ownerId) return;
      const status = inviteStatusOf(invites?.[a.id] || null);
      if (status === INVITE_STATUS.YES) out.yes += 1;
      else if (status === INVITE_STATUS.NO) out.no += 1;
      else if (status === INVITE_STATUS.INVITED) out.pending += 1;
    });
    return out;
  })();
  const hasInviteTotals = inviteTotals.yes + inviteTotals.no + inviteTotals.pending > 0;

  // Access-level badge — episodes carry an `access` field ('free' by default,
  // or a membership tier). Only shown for episode-type events; user-events and
  // shows have no access concept. Org episodes may not resolve a tier name, so
  // fall back to a generic "Members only" label.
  const isEpisodeEvent = type === 'episode' || type === 'assigned-episode';
  const accessLevel = event?.access;
  const membershipTierName = event?.membershipTierName;
  const showAccessBadge = isEpisodeEvent && !!accessLevel;

  // Every episode has its own conversation thread; this is the one click from
  // the calendar to the people in it. The channel url is derived rather than
  // looked up — it's `episode_<listingId>_<episodeId>` by construction.
  const episodeListingId = event?.parentLiveRoomId || event?.listingId;
  const episodeThreadId =
    isEpisodeEvent && episodeListingId && event?.exceptionId
      ? encodeChannelId(
          `${CHANNEL_TYPES.EPISODE}_${episodeListingId}_${event.episodeId || event.exceptionId}`
        )
      : null;

  return (
    <div className={css.eventDetails}>
      {/* Event Title */}
      <h2 className={css.eventTitle}>{displayTitle}</h2>

      {/* Access-level badge (Free / Members-only) for episodes */}
      {showAccessBadge ? (
        <div className={css.accessBadgeRow}>
          <Pill tone={accessLevel === 'free' ? 'neutral' : 'success'}>
            {accessLevel === 'free' ? (
              <FormattedMessage id="CalendarEventModal.accessFree" />
            ) : membershipTierName ? (
              <FormattedMessage
                id="CalendarEventModal.accessTier"
                values={{ tier: membershipTierName }}
              />
            ) : (
              <FormattedMessage id="CalendarEventModal.accessMembersOnly" />
            )}
          </Pill>
        </div>
      ) : null}

      {/* Who has reserved a spot, and the one click to talk to them. Kept as
          a single block on purpose (Yuko, 1 Sep 2026): the RSVP stack and the
          chat link are the same social signal, so they sit together rather
          than as two unrelated rows. Only rendered for episodes — a manual
          user event has its own invited-attendee list, and a show has neither. */}
      {isEpisodeEvent ? (
        <div className={css.detailRow}>
          <div className={css.detailIcon}>
            <Icon name="users" />
          </div>
          <div className={classNames(css.detailContent, css.socialBlock)}>
            <RsvpAvatarStack
              total={rsvpStack?.total || 0}
              canSeeIdentities={!!rsvpStack?.canSeeIdentities}
              attendees={rsvpStack?.attendees || []}
              avatarLimit={rsvpStack?.avatarLimit || 5}
            />
            {episodeThreadId ? (
              <NamedLink
                className={css.chatCta}
                name={orgId ? 'OrgConversationsPage' : 'CreatorConversationsPage'}
                params={
                  orgId ? { orgId, channelId: episodeThreadId } : { channelId: episodeThreadId }
                }
              >
                <Icon name="message" />
                {/* "Chat with participants" is optimistic when nobody has
                    reserved yet; the thread exists either way. */}
                {(rsvpStack?.total || 0) > 0 ? (
                  <FormattedMessage id="CalendarEventModal.chatWithParticipants" />
                ) : (
                  <FormattedMessage id="CalendarEventModal.openEpisodeChat" />
                )}
              </NamedLink>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Rented badge — shown on calendar entries that come from an
          active Creator assignment. The org sees the source so it's
          clear the episode isn't theirs to edit. */}
      {event?.type === 'assigned-episode' && event?.provenance ? (
        <div className={css.rentedBadgeRow}>
          <RentedBadge
            creatorName={event.provenance.creatorName}
            daysRemaining={
              event.provenance.expiresAt
                ? Math.max(0, daysBetween(new Date().toISOString(), event.provenance.expiresAt))
                : null
            }
          />
        </div>
      ) : null}

      {/* Date & Time */}
      {dateDisplay && (
        <div className={css.detailRow}>
          <div className={css.detailIcon}>
            <Icon name="calendar" />
          </div>
          <div className={css.detailContent}>
            <span className={css.detailValue}>{dateDisplay}</span>
          </div>
        </div>
      )}

      {/* Location */}
      {location && (
        <div className={css.detailRow}>
          <div className={css.detailIcon}>
            <Icon name="location" />
          </div>
          <div className={css.detailContent}>
            <span className={css.detailValue}>
              {typeof location === 'object' ? location.address : location}
            </span>
          </div>
        </div>
      )}

      {/* Attendees */}
      {hasAttendees && (
        <div className={css.detailRow}>
          <div className={css.detailIcon}>
            <Icon name="users" />
          </div>
          <div className={css.detailContent}>
            {hasInviteTotals ? (
              <p className={css.inviteTotals}>
                <FormattedMessage id="CalendarEventModal.inviteTotals" values={inviteTotals} />
              </p>
            ) : null}
            <div className={css.attendeesList}>
              {attendees.map((attendee, index) => (
                <AttendeeItem
                  key={attendee.id || index}
                  attendee={attendee}
                  isOwner={attendee.id === ownerId || attendee.userId === ownerId}
                  inviteEntry={invites?.[attendee.id] || null}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Description */}
      {description && (
        <div className={css.detailRow}>
          <div className={css.detailIcon}>
            <Icon name="document" />
          </div>
          <div className={css.detailContent}>
            <span className={css.descriptionText}>{description}</span>
          </div>
        </div>
      )}

      {/* Meeting Link */}
      {meetingLink && (
        <div className={css.detailRow}>
          <div className={css.detailIcon}>
            <Icon name="video" />
          </div>
          <div className={css.detailContent}>
            <div className={css.meetingActions}>
              {meetingLink.startsWith('/') ? (
                <NamedLink
                  name="MeetPage"
                  params={{ roomName: meeting?.roomName?.replace(/^\/+/, '') }}
                  className={css.joinMeetingButton}
                >
                  <FormattedMessage id="CalendarEventModal.joinMeeting" />
                </NamedLink>
              ) : (
                <ExternalLink href={meetingLink} className={css.joinMeetingButton}>
                  <FormattedMessage id="CalendarEventModal.joinMeeting" />
                </ExternalLink>
              )}
              <CopyLinkButton meetingLink={shareLink} intl={intl} />
            </div>
            {isMeetingHost && (
              <p className={css.meetingHostNote}>
                <FormattedMessage
                  id="CalendarEventModal.hostNote"
                  defaultMessage="You're the host — you'll join with host controls. Copy shares the guest link."
                />
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Modal Component
const CalendarEventModal = props => {
  const {
    className,
    rootClassName,
    id = 'CalendarEventModal',
    isOpen,
    onClose,
    onManageDisableScrolling,
    event,
    timeZone,
    currentUserId,
    intl,
    // Edit/Delete actions for user events
    showEditButton,
    showDeleteButton,
    onEdit,
    onDelete,
    deleteInProgress,
    // Episode actions
    showDeleteEpisodeButton,
    onDeleteEpisode,
    deleteEpisodeInProgress,
    onEditEpisode,
    // Show/Tour schedule actions
    showDeleteShowButton,
    onDeleteShow,
    deleteShowInProgress,
    onEditShow,
    orgId,
    rsvpStack,
    invites,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  if (!event) {
    return null;
  }

  return (
    <Modal
      id={id}
      containerClassName={classes}
      contentClassName={css.modalContent}
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
    >
      <EventDetails
        event={event}
        intl={intl}
        timeZone={timeZone}
        currentUserId={currentUserId}
        orgId={orgId}
        rsvpStack={rsvpStack}
        invites={invites}
      />

      {/* Edit/Delete Actions for user events */}
      {(showEditButton || showDeleteButton) && (
        <div className={css.actionButtons}>
          {showDeleteButton && onDelete && (
            <button
              type="button"
              className={css.deleteButton}
              onClick={onDelete}
              disabled={deleteInProgress}
            >
              {deleteInProgress ? (
                <FormattedMessage id="CalendarEventModal.deleting" />
              ) : (
                <FormattedMessage id="CalendarEventModal.deleteButton" />
              )}
            </button>
          )}
          {showEditButton && onEdit && (
            <button type="button" className={css.editButton} onClick={onEdit}>
              <FormattedMessage id="CalendarEventModal.editButton" />
            </button>
          )}
        </div>
      )}

      {/* Edit/Delete Actions for episodes */}
      {(showDeleteEpisodeButton || event?.listingId) && event?.type === 'episode' && (
        <div className={css.actionButtons}>
          {showDeleteEpisodeButton && onDeleteEpisode && (
            <button
              type="button"
              className={css.deleteButton}
              onClick={onDeleteEpisode}
              disabled={deleteEpisodeInProgress}
            >
              {deleteEpisodeInProgress ? (
                <FormattedMessage id="CalendarEventModal.deletingEpisode" />
              ) : (
                <FormattedMessage id="CalendarEventModal.deleteEpisodeButton" />
              )}
            </button>
          )}
          {event?.listingId && (
            <NamedLink
              name="EditLiveRoomPage"
              params={{
                slug: createSlug(event?.listingTitle || 'live-room'),
                id: event.listingId,
                type: LISTING_PAGE_PARAM_TYPE_EDIT,
                tab: 'schedules',
              }}
              className={css.editLinkButton}
            >
              <FormattedMessage id="CalendarEventModal.editEpisodeButton" />
            </NamedLink>
          )}
        </div>
      )}

      {/* Read-only "View Live Room" action for episodes that come
          from an active Creator assignment. No edit/delete — those
          live with the creator. */}
      {event?.type === 'assigned-episode' && event?.liveRoomId ? (
        <div className={css.actionButtons}>
          <NamedLink
            name="LiveRoomPage"
            params={{
              slug: createSlug(event?.liveRoomTitle || 'live-room'),
              id: event.liveRoomId,
            }}
            className={css.editLinkButton}
          >
            <FormattedMessage id="CalendarEventModal.viewLiveRoomButton" />
          </NamedLink>
        </div>
      ) : null}

      {/* Edit/Delete Actions for shows/tour schedules */}
      {(showDeleteShowButton || event?.listingId) && event?.type === PAGE_TYPE && (
        <div className={css.actionButtons}>
          {showDeleteShowButton && onDeleteShow && (
            <button
              type="button"
              className={css.deleteButton}
              onClick={onDeleteShow}
              disabled={deleteShowInProgress}
            >
              {deleteShowInProgress ? (
                <FormattedMessage id="CalendarEventModal.deletingTourDate" />
              ) : (
                <FormattedMessage id="CalendarEventModal.deleteTourDateButton" />
              )}
            </button>
          )}
          {event?.listingId && (
            <NamedLink
              name="EditListingPage"
              params={{
                slug: createSlug(event?.listingTitle || 'page'),
                id: event.listingId,
                type: LISTING_PAGE_PARAM_TYPE_EDIT,
                tab: 'touring-links',
              }}
              className={css.editLinkButton}
            >
              <FormattedMessage id="CalendarEventModal.editPageButton" />
            </NamedLink>
          )}
        </div>
      )}
    </Modal>
  );
};

const { bool, string, func, object } = PropTypes;

CalendarEventModal.defaultProps = {
  className: null,
  rootClassName: null,
  id: null,
  isOpen: false,
  event: null,
  timeZone: null,
  showEditButton: false,
  showDeleteButton: false,
  onEdit: null,
  onDelete: null,
  deleteInProgress: false,
  // Episode props
  showDeleteEpisodeButton: false,
  onDeleteEpisode: null,
  deleteEpisodeInProgress: false,
  onEditEpisode: null,
  // Show/Tour schedule props
  showDeleteShowButton: false,
  onDeleteShow: null,
  deleteShowInProgress: false,
  onEditShow: null,
};

CalendarEventModal.propTypes = {
  className: string,
  rootClassName: string,
  id: string,
  intl: intlShape.isRequired,
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  event: object,
  timeZone: string,
  showEditButton: bool,
  showDeleteButton: bool,
  onEdit: func,
  onDelete: func,
  deleteInProgress: bool,
  // Episode props
  showDeleteEpisodeButton: bool,
  onDeleteEpisode: func,
  deleteEpisodeInProgress: bool,
  onEditEpisode: func,
  // Show/Tour schedule props
  showDeleteShowButton: bool,
  onDeleteShow: func,
  deleteShowInProgress: bool,
  onEditShow: func,
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// The stack is fetched by whichever calendar page opened this modal (see their
// event-click handlers); here we only read the slice for the episode on screen.
const mapStateToProps = (state, ownProps) => {
  const event = ownProps.event;
  const listingId = event?.parentLiveRoomId || event?.listingId;
  const key = listingId && event?.exceptionId ? attendeeKey(listingId, event.exceptionId) : null;
  return {
    rsvpStack: key ? state.episodeAttendees.byEpisode[key] || null : null,
  };
};

export default compose(
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps)
)(CalendarEventModal);
